import React, { useState, useEffect, useCallback } from 'react';
import { employeeApi, attendanceApi, leaveApi, payrollApi } from '@/lib/api';
import { clampPageToTotal } from '@/lib/pagination';
import { formatCurrency } from '@/lib/payrollCalc';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Download, FileBarChart, Users, CalendarClock, Receipt } from 'lucide-react';
import { exportRowsToExcel } from '@/lib/exportFile';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const normalizeArrayResponse = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};

const normalizeListResponse = (value) => {
  if (Array.isArray(value)) return { items: value, total: value.length, page: 1, pages: 1 };
  if (Array.isArray(value?.items)) return { items: value.items, total: value?.total ?? value.items.length, page: value?.page ?? 1, pages: value?.pages ?? 1 };
  if (Array.isArray(value?.employees)) return { items: value.employees, total: value?.total ?? value.employees.length, page: value?.page ?? 1, pages: value?.pages ?? 1 };
  if (Array.isArray(value?.leaves)) return { items: value.leaves, total: value?.total ?? value.leaves.length, page: value?.page ?? 1, pages: value?.pages ?? 1 };
  return { items: [], total: 0, page: 1, pages: 1 };
};

export default function ReportsPage() {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [reportType, setReportType] = useState('attendance');
  const [selectedMonth, setSelectedMonth] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const buildAttendanceRows = (employees, attendance, monthPrefix) => {
    return employees.map(emp => {
      const empId = emp.empId || emp.id;
      const empAtt = attendance.filter(a => a.employeeId === empId && a.date?.startsWith(monthPrefix));
      return {
        'Employee ID': empId,
        'Name': `${emp.firstName} ${emp.lastName}`,
        'Department': emp.department,
        'Present Days': empAtt.filter(a => a.status === 'Present').length,
        'Absent Days': empAtt.filter(a => a.status === 'Absent').length,
        'Late Days': empAtt.filter(a => a.isLate).length,
        'Total Hours': Math.round(empAtt.reduce((s, a) => s + (a.workingHours || 0), 0) * 10) / 10,
      };
    });
  };

  const buildLeaveRows = (leaves) => {
    return leaves.map(l => ({
      'Leave ID': l.leaveId || l.id,
      'Employee': l.employeeName,
      'Type': l.type,
      'Start Date': l.startDate,
      'End Date': l.endDate,
      'Status': l.status,
      'Reason': l.reason,
      'Applied On': new Date(l.appliedOn).toLocaleDateString(),
    }));
  };

  const buildPayrollRows = (payRuns, month, year) => {
    const pr = payRuns.find(p => p.month === parseInt(month) && p.year === parseInt(year));
    if (!pr?.employees) return [];
    return pr.employees.map(e => ({
      'Employee ID': e.employeeId,
      'Name': e.employeeName,
      'Department': e.department,
      'Basic': e.basic,
      'HRA': e.hra,
      'Gross': e.effectiveGross,
      'PF': e.pf,
      'ESI': e.esi,
      'TDS': e.tds,
      'Net Pay': e.netPay,
    }));
  };

  const buildEmployeeRows = (employees) => {
    return employees.map(emp => ({
      'Employee ID': emp.empId || emp.id,
      'Name': `${emp.firstName} ${emp.lastName}`,
      'Department': emp.department,
      'Designation': emp.designation,
      'Company': emp.company,
      'Status': emp.status,
      'Joining Date': emp.joiningDate,
      'Phone': emp.phone,
      'Email': emp.email,
    }));
  };

  useEffect(() => {
    const fetchPagedData = async () => {
      setLoading(true);
      try {
        const monthPrefix = `${selectedYear}-${selectedMonth}`;

        if (reportType === 'attendance') {
          const [empsRes, attData] = await Promise.all([
            employeeApi.getAll({ page, limit: pageSize }).catch(() => []),
            attendanceApi.get({ month: selectedMonth, year: selectedYear }).catch(() => []),
          ]);
          const normalized = normalizeListResponse(empsRes);
          const rows = buildAttendanceRows(normalized.items, normalizeArrayResponse(attData), monthPrefix);
          setReportData(rows);
          setTotalRecords(normalized.total);
          setTotalPages(normalized.pages);
          setPage((p) => clampPageToTotal(p, normalized.pages));
        } else if (reportType === 'leaves') {
          const leavesRes = await leaveApi.getAll({ page, limit: pageSize }).catch(() => []);
          const normalized = normalizeListResponse(leavesRes);
          setReportData(buildLeaveRows(normalized.items));
          setTotalRecords(normalized.total);
          setTotalPages(normalized.pages);
          setPage((p) => clampPageToTotal(p, normalized.pages));
        } else if (reportType === 'payroll') {
          const payRunsData = await payrollApi.getAll({ month: selectedMonth, year: selectedYear }).catch(() => []);
          const payRuns = normalizeArrayResponse(payRunsData);
          const rows = buildPayrollRows(payRuns, selectedMonth, selectedYear);
          setReportData(rows);
          setTotalRecords(rows.length);
          setTotalPages(1);
        } else if (reportType === 'employees') {
          const empsRes = await employeeApi.getAll({ page, limit: pageSize }).catch(() => []);
          const normalized = normalizeListResponse(empsRes);
          setReportData(buildEmployeeRows(normalized.items));
          setTotalRecords(normalized.total);
          setTotalPages(normalized.pages);
          setPage((p) => clampPageToTotal(p, normalized.pages));
        }
      } catch (err) {
        console.error('Failed to fetch report data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPagedData();
  }, [reportType, selectedMonth, selectedYear, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [reportType, selectedMonth, selectedYear]);

  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const monthPrefix = `${selectedYear}-${selectedMonth}`;
      let data = [];

      if (reportType === 'attendance') {
        const [empsData, attData] = await Promise.all([
          employeeApi.getAll().catch(() => []),
          attendanceApi.get({ month: selectedMonth, year: selectedYear }).catch(() => []),
        ]);
        data = buildAttendanceRows(normalizeArrayResponse(empsData), normalizeArrayResponse(attData), monthPrefix);
      } else if (reportType === 'leaves') {
        const leavesData = await leaveApi.getAll().catch(() => []);
        data = buildLeaveRows(normalizeArrayResponse(leavesData));
      } else if (reportType === 'payroll') {
        const payRunsData = await payrollApi.getAll({ month: selectedMonth, year: selectedYear }).catch(() => []);
        data = buildPayrollRows(normalizeArrayResponse(payRunsData), selectedMonth, selectedYear);
      } else if (reportType === 'employees') {
        const empsData = await employeeApi.getAll().catch(() => []);
        data = buildEmployeeRows(normalizeArrayResponse(empsData));
      }

      if (data.length === 0) {
        toast.error('No data to export');
        return;
      }

      exportRowsToExcel(data, {
        fileName: `${reportType}_report_${selectedYear}_${selectedMonth}`,
        sheetName: reportType,
      });
      toast.success(`Report exported (${data.length} records)`);
    } catch (err) {
      toast.error(err?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  }, [reportType, selectedMonth, selectedYear, exporting]);

  const columns = reportData.length > 0 ? Object.keys(reportData[0]) : [];

  const reportTypes = [
    { value: 'attendance', label: 'Attendance Summary', icon: CalendarClock },
    { value: 'leaves', label: 'Leave Report', icon: FileBarChart },
    { value: 'payroll', label: 'Payroll Report', icon: Receipt },
    { value: 'employees', label: 'Employee Directory', icon: Users },
  ];

  const isCurrency = (col) => ['Basic', 'HRA', 'Gross', 'PF', 'ESI', 'TDS', 'Net Pay'].includes(col);

  if (loading && reportData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047AB] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="reports-page" className="space-y-3 animate-fadeIn">
      <div>
        <h1 className="text-3xl font-bold tracking-tight font-[Manrope]">Reports</h1>
        <p className="text-muted-foreground mt-1">Generate and export reports</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {reportTypes.map(rt => (
          <button
            key={rt.value}
            data-testid={`report-type-${rt.value}`}
            onClick={() => setReportType(rt.value)}
            className={`p-4 rounded-lg border text-left transition-all ${
              reportType === rt.value
                ? 'border-[#0047AB] bg-[#0047AB]/5 shadow-sm'
                : 'border-border hover:border-[#0047AB]/30'
            }`}
          >
            <rt.icon className={`w-5 h-5 mb-2 ${reportType === rt.value ? 'text-[#0047AB]' : 'text-muted-foreground'}`} />
            <p className={`text-sm font-medium ${reportType === rt.value ? 'text-[#0047AB]' : ''}`}>{rt.label}</p>
          </button>
        ))}
      </div>

      <Card className="border border-border shadow-sm">
        <CardContent className="p-4 flex flex-wrap items-end gap-4">
          {(reportType === 'attendance' || reportType === 'payroll') && (
            <>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-1.5">Month</label>
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="w-[140px]" data-testid="report-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1).padStart(2, '0')}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-1.5">Year</label>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="w-[100px]" data-testid="report-year"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2025">2025</SelectItem>
                    <SelectItem value="2026">2026</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="bg-[#0047AB] hover:bg-[#003d96] text-white ml-auto"
            data-testid="export-report-btn"
          >
            <Download className="w-4 h-4 mr-2" /> {exporting ? 'Exporting...' : 'Export'}
          </Button>
        </CardContent>
      </Card>

      <Card className="border border-border shadow-sm overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base font-[Manrope]">
            {reportTypes.find(r => r.value === reportType)?.label} Preview
          </CardTitle>
          <Badge variant="outline" className="font-mono text-xs">{totalRecords} records</Badge>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 sticky top-0 z-10">
                  {columns.map(col => (
                    <TableHead key={col} className="text-xs uppercase tracking-widest font-medium whitespace-nowrap">{col}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {reportData.map((row, i) => (
                  <TableRow key={i}>
                    {columns.map(col => (
                      <TableCell key={col} className={`text-sm whitespace-nowrap ${isCurrency(col) ? 'font-mono' : ''}`}>
                        {isCurrency(col) ? formatCurrency(row[col]) : row[col]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {totalRecords === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length || 1} className="text-center py-12 text-muted-foreground">
                      <FileBarChart className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      No data available for this report
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages} ({totalRecords} records)
          </p>
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-muted-foreground">Show</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => {
                setPageSize(Number(v));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[70px] h-8 text-sm" data-testid="reports-page-size-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">per page</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={page <= 1}
            data-testid="reports-prev-page-btn"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={page >= totalPages}
            data-testid="reports-next-page-btn"
          >
            Next
          </Button>
        </div>
      </div>

    </div>
  );
}
